import { IconUserFilled, IconUserMinus } from '@rossum/ui/icons/tabler';
import {
  Box,
  Button,
  Dialog,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { useCallback, useMemo, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { connect, useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import * as R from 'remeda';
import ContentWrapper from '../../components/ContentWrapper/index';
import PageLayout from '../../components/PageLayout/index';
import { DEV_FEATURES_ENABLED } from '../../constants/config';
import {
  isFeatureEnabled,
  isFeatureGloballyEnabled,
  isFeatureLocallyEnabled,
  setFeatureLocalOverride,
} from '../../features/feature-enablement/featureEnablement';
import {
  allFeatures,
  allFeaturesList,
  FeatureId,
  labFeatures,
  labFeaturesList,
} from '../../features/feature-enablement/features';
import { FeatureToggles } from '../../features/feature-enablement/FeatureToggles';
import { useCreateSurvey } from '../../features/surveys/hooks/useCreateSurvey';
import { usePatchSurvey } from '../../features/surveys/hooks/usePatchSurvey';
import { surveysUuidMap } from '../../features/surveys/surveysUuidMap';
import { notify } from '../../features/toaster';
import { getRoleName } from '../../redux/modules/groups/helpers';
import {
  PartialUser,
  updateUiSettings,
  updateUser as updateUserAction,
} from '../../redux/modules/user/actions';
import { getName } from '../../redux/modules/user/helpers';
import { GroupRole } from '../../types/group';
import { State } from '../../types/state';
import { User } from '../../types/user';
import DeleteUserDialog from '../Users/components/DeleteUser/DeleteUserDialog';
import Section from './components/Section';
import ChangePasswordForm from './containers/ChangePasswordForm';
import { DiscoveryCallsOverview } from './containers/DiscoveryCallsOverview';
import PersonalInformationForm from './containers/PersonalInformationForm';
import Sidebar from './containers/Sidebar';

type OwnProps = {
  user: User;
  role?: GroupRole;
  updateUser: (_payload: PartialUser, _meta: object) => void;
} & RouteComponentProps<{ section: string }>;
type IntlProps = { intl: IntlShape };

type Props = OwnProps & IntlProps;

const PersonalInfo = ({
  updateUser,
  user,
  role,
  intl,
  match: {
    params: { section },
  },
  location: { key },
}: Props) => {
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();

  // Trick to rerender after changing local storage without having to deal with subscriptions and stuff
  // Open to better suggestions (we could put this into zustand or something)
  // eslint-disable-next-line react/hook-use-state
  const [rerenderState, rerender] = useState(0);

  const visibleLabFeatures = useMemo(
    () =>
      labFeaturesList.filter(
        feature =>
          isFeatureLocallyEnabled(feature.id) ||
          isFeatureGloballyEnabled(feature.id)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rerenderState]
  );

  const userLabConfig = useMemo(
    () => user.uiSettings?.labConfig || {},
    [user.uiSettings?.labConfig]
  );

  const featureConfig = useMemo(() => {
    return R.mapValues(labFeatures, feature => ({
      enabled: isFeatureEnabled(feature.id, userLabConfig),
      askForFeedback: !userLabConfig[feature.id]?.sentFeedback,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLabConfig, rerenderState]);

  const handleFeatureChanged = useCallback(
    (featureId: FeatureId, value: boolean | null) => {
      dispatch(
        updateUiSettings({
          labConfig:
            value === null
              ? undefined
              : {
                  ...userLabConfig,
                  [featureId]: {
                    enabled: value,
                    lastUpdatedAt: new Date().toISOString(),
                  },
                },
        })
      );
    },
    [dispatch, userLabConfig]
  );

  const localFeaturesConfig = useMemo(() => {
    return R.mapValues(allFeatures, feature => ({
      enabled: isFeatureEnabled(feature.id, userLabConfig),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLabConfig, rerenderState]);

  const handleLocalFeatureOverrideChanged = useCallback(
    (featureId: FeatureId, value: boolean | null) => {
      setFeatureLocalOverride(featureId, value);
      rerender(prev => prev + 1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rerender, rerenderState]
  );

  const { mutateAsync: createSurvey } = useCreateSurvey(
    surveysUuidMap.featureFeedbackSurvey
  );

  const { mutateAsync: updateSurvey } = usePatchSurvey();

  const setFeedbackSent = useCallback(
    (featureId: FeatureId) => {
      dispatch(
        updateUiSettings({
          labConfig: {
            [featureId]: {
              sentFeedback: true,
            },
          },
        })
      );
    },
    [dispatch]
  );

  const handleFeedbackSubmitted = useCallback(
    (featureId: FeatureId, feedback: string) => {
      if (feedback.length) {
        createSurvey()
          .then(survey => {
            updateSurvey({
              surveyId: survey.id,
              surveyPatchModel: {
                answers: [
                  {
                    value: feedback,
                  },
                ],
                additionalData: {
                  featureId,
                },
              },
            })
              .then(() => {
                setFeedbackSent(featureId);
                notify.success({
                  title: 'Your feedback was submitted successfully',
                });
              })
              .catch(() => {
                notify.error({
                  title: 'Error submitting feedback',
                });
              });
          })
          .catch(() => {
            notify.error({
              title: 'Error submitting feedback',
            });
          });
      }
    },
    [createSurvey, updateSurvey, setFeedbackSent]
  );

  const handleFeedbackRejected = useCallback(
    (featureId: FeatureId) => {
      setFeedbackSent(featureId);
    },
    [setFeedbackSent]
  );

  const activeRoute = { section, key };

  return (
    <PageLayout data-page-title="personal-info">
      <Sidebar />
      <ContentWrapper>
        <Stack
          spacing={1}
          sx={{
            alignItems: 'center',
            width: '100%',
            height: '100%',
            overflowY: 'auto',
          }}
        >
          <Stack spacing={3} sx={{ maxWidth: '960px', p: 3, pt: 6 }}>
            <Stack direction="row" spacing={1} sx={{ width: '960px' }}>
              <Stack direction="row" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '100%',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: theme => theme.palette.grey['700'],
                  }}
                >
                  <SvgIcon fontSize="small" color="primary">
                    <IconUserFilled />
                  </SvgIcon>
                </Box>
                <Stack spacing={0.5}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {getName(user)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email || user.username}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            <Stack>
              <Stack spacing={4}>
                <Section
                  title="containers.personalInfo.personalInformation"
                  id="personalInfo"
                  activeRoute={activeRoute}
                >
                  <PersonalInformationForm
                    updateUser={updateUser}
                    intl={intl}
                    user={user}
                  />
                </Section>
                <Section
                  title="containers.personalInfo.changePassword"
                  id="changePassword"
                  activeRoute={activeRoute}
                >
                  <ChangePasswordForm username={user.username} />
                </Section>

                {visibleLabFeatures.length > 0 ? (
                  <Section
                    title="containers.personalInfo.rossumLabs"
                    id="rossumLabs"
                    activeRoute={activeRoute}
                  >
                    <FeatureToggles
                      features={visibleLabFeatures}
                      config={featureConfig}
                      onChange={handleFeatureChanged}
                      collectFeedback
                      onFeedbackSubmit={handleFeedbackSubmitted}
                      onFeedbackReject={handleFeedbackRejected}
                    />
                  </Section>
                ) : null}

                {DEV_FEATURES_ENABLED ? (
                  <Section
                    title="containers.personalInfo.changeFeatureFlags"
                    id="changeFeatureFlags"
                    activeRoute={activeRoute}
                  >
                    <FeatureToggles
                      features={allFeaturesList}
                      config={localFeaturesConfig}
                      onChange={handleLocalFeatureOverrideChanged}
                      clearable
                    />
                  </Section>
                ) : null}

                {DEV_FEATURES_ENABLED ? (
                  <Section
                    title="containers.personalInfo.discoveryCallSurveys"
                    id="discoveryCallSurveys"
                    activeRoute={activeRoute}
                  >
                    <DiscoveryCallsOverview />
                  </Section>
                ) : null}
              </Stack>

              <Button
                variant="outlined"
                data-cy="delete-account-button"
                color="error"
                onClick={() => setOpen(true)}
                sx={{ mt: 4, alignSelf: 'flex-start' }}
                startIcon={
                  <SvgIcon fontSize="small">
                    <IconUserMinus />
                  </SvgIcon>
                }
              >
                {intl.formatMessage({
                  id: 'containers.personalInfo.deleteAccount.button',
                })}
              </Button>
              <Dialog
                open={open}
                PaperProps={{
                  sx: { width: 480, minHeight: 213, position: 'fixed' },
                  elevation: 2,
                }}
                onClose={() => setOpen(false)}
                sx={{ transition: 'smooth' }}
              >
                {open ? (
                  <DeleteUserDialog
                    user={{ ...user, role }}
                    onCancel={() => setOpen(false)}
                  />
                ) : null}
              </Dialog>
            </Stack>
          </Stack>
        </Stack>
      </ContentWrapper>
    </PageLayout>
  );
};

const mapStateToProps = (state: State) => {
  const { user } = state;
  const role = getRoleName(user.groups, state.groups);

  return { user, role };
};

const mapDispatchToProps = {
  updateUser: updateUserAction,
};

export default injectIntl<'intl', OwnProps & IntlProps>(
  connect(mapStateToProps, mapDispatchToProps)(PersonalInfo)
);
